import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Phone, Mail, Clock, Facebook, Twitter } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="bg-black text-gold-400 border-t border-white/10 mt-auto relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-gold-500 to-transparent opacity-50 shadow-neon-gold"></div>

      <div className="container mx-auto px-6 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Section */}
          <div className="space-y-8">
            <div className="flex items-center gap-4">
              <div className="relative w-16 h-16 group">
                <div className="absolute inset-0 bg-gold-500 rounded-full blur opacity-20 group-hover:opacity-40 transition-opacity duration-300 animate-pulse-slow"></div>
                <img
                  src="/logo.jpg"
                  alt="Magic World Logo"
                  className="w-full h-full object-cover rounded-full border-2 border-gold-500 relative z-10 shadow-lg shadow-gold-500/20 group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div>
                <h3 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gold-300 via-gold-500 to-gold-300 font-serif tracking-wide">
                  Magic World
                </h3>
                <p className="text-xs text-gold-400/80 tracking-[0.3em] uppercase font-light">Premium Cloud Kitchen</p>
              </div>
            </div>
            <p className="text-gray-400 leading-relaxed text-sm">
              Experience the magic of culinary excellence. We serve premium, handcrafted meals with a touch of elegance, delivered straight to your doorstep.
            </p>
            <div className="flex gap-4 pt-2">
              <button className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-gold-500 hover:text-black transition-all duration-300">
                <Facebook className="w-5 h-5" />
              </button>
              <a href="https://www.instagram.com/mw_cook_with_magic" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-gold-500 hover:text-black transition-all duration-300">
                <Instagram className="w-5 h-5" />
              </a>
              <button className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-gold-500 hover:text-black transition-all duration-300">
                <Twitter className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
              <span className="w-8 h-0.5 bg-gold-500"></span> Quick Links
            </h4>
            <div className="space-y-3">
              {[
                { to: '/menu', label: 'Our Menu' },
                { to: '/deals', label: 'Special Deals' },
                { to: '/reviews', label: 'Customer Reviews' },
                { to: '/about', label: 'About Us' },
                { to: '/contact', label: 'Contact Support' },
                { to: '/track-order', label: 'Track Your Order' },
              ].map((link, idx) => (
                <Link
                  key={idx}
                  to={link.to}
                  className="block text-gray-400 hover:text-gold-400 hover:translate-x-2 transition-all duration-300 text-sm"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
              <span className="w-8 h-0.5 bg-gold-500"></span> Contact Us
            </h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3 text-gray-400 group">
                <Phone className="w-5 h-5 text-gold-500 mt-1 group-hover:scale-110 transition-transform" />
                <div>
                  <span className="block text-white text-sm font-medium">Inquiries</span>
                  <span className="text-sm">+92 333 5228094</span>
                </div>
              </div>
              <div className="flex items-start gap-3 text-gray-400 group">
                <Mail className="w-5 h-5 text-gold-500 mt-1 group-hover:scale-110 transition-transform" />
                <div>
                  <span className="block text-white text-sm font-medium">Email Us</span>
                  <span className="text-sm">magicworld862@gmail.com</span>
                </div>
              </div>
              <div className="flex items-start gap-3 text-gray-400 group">
                <Clock className="w-5 h-5 text-gold-500 mt-1 group-hover:scale-110 transition-transform" />
                <div>
                  <span className="block text-white text-sm font-medium">Operating Hours</span>
                  <span className="text-sm">Mon - Sun: 10:00 AM - 11:00 PM</span>
                </div>
              </div>
            </div>
          </div>

          {/* Instagram Promo */}
          <div>
            <h4 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
              <span className="w-8 h-0.5 bg-gold-500"></span> Follow Us
            </h4>
            <div className="bg-gradient-to-br from-purple-900/20 to-pink-900/20 p-6 rounded-2xl border border-white/10 text-center group hover:border-gold-500/30 transition-all duration-300">
              <Instagram className="w-10 h-10 text-pink-500 mx-auto mb-4 group-hover:scale-110 transition-transform duration-300" />
              <p className="text-gray-300 text-sm mb-4">Stay updated with our latest mouth-watering creations and offers!</p>
              <a
                href="https://www.instagram.com/mw_cook_with_magic?igsh=MTU2YjVnNXN4dzRhNA=="
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-gradient-to-r from-pink-600 to-purple-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg shadow-pink-500/20 hover:shadow-pink-500/40 hover:-translate-y-1 transition-all duration-300"
              >
                @magicworld.pk
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-white/5 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-gray-500">
          <p>&copy; {new Date().getFullYear()} Magic World. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-gold-400 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-gold-400 transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

